import React, { FC, useEffect, useState } from 'react';
import { ContentHeader } from '../shared/containers/content-header';
import { Button, MenuItem, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Content } from '../shared/containers/content';
import { useCarrier, usePermissions } from '../user/selectors/use-permissions';
import { PermissionType } from 'harmony-constants';
import { useDraftLoadColumns } from './hooks/use-draft-load-columns';
import { DraftLoadTable } from './table-components/draft-load-table';
import { useTravelTimesQuery } from '../travel-times/use-travel-times';
import { LoadTableToolbar } from './load-table-toolbar';
import { useDraftLoadTable } from './hooks/use-draft-load-table';
import { Load } from '../../types';
import { useDraftsLoads } from '../../api/queries/use-drafts-loads';
import { DraftLoadModals } from './draft-load-modals';
import { L } from 'harmony-language';
import { useUser } from '../../api/queries/use-user';
import { getPageSize, globalFilterRemover } from './utils/drafts-loads-utils';
import { SelectionButtons } from './selection-buttons';
import { DraftsLoadsProvider } from './drafts-loads-context';
import { SocketListener } from '../socket-listener/socket-listener';
import { MenuButton } from '../shared/inputs/menu-button';

interface DraftsLoadsProps {
    isDrafts: boolean;
}

export type DraftsLoadsModal = null | 'editCreateLoad' | 'deliveryLog' | 'unmergeLoad'
    | 'createDrafts' | 'bulkModifyDrafts' | 'mergeDrafts' | 'autoMergeDrafts' | 'cascadeDrafts' | 'bulkScheduleDrafts'
    | 'completeLoads' | 'assignLoads' | 'publishDrafts' | 'assignCarrier' | 'deleteRecords' | 'createDraft';

export const DraftsLoads: FC<DraftsLoadsProps> = (props) => {
    const { isDrafts } = props;

    const draftsLoadsQuery = useDraftsLoads(isDrafts);
    useTravelTimesQuery(draftsLoadsQuery.isFetching);

    const canCreateOrder = usePermissions(PermissionType.CreateOrder);
    const isCarrier = useCarrier();

    const [dialogOpen, setDialogOpen] = useState<DraftsLoadsModal>(null);
    const [singleSelectedLoad, setSingleSelectedLoad] = useState<Load | null>(null);
    // I don't like this, but I can't figure out a better way of doing it.  Problem I have is default
    // preset should only apply on 1st page load and when switching from one page to the other.
    // Can't do this logic in MultiFilter because it's re-rendered on date change which at that time
    // default presets should not be applied.  Can't apply the filter in the parent because facetedKeys are not known
    // and user may have a default preset that may or may not have matching data in the result set.
    const [applyDefaultPreset, setApplyDefaultPreset] = useState(true);

    const handleEditableLoadOpen = (load: Load) => {
        setSingleSelectedLoad(load);
        setDialogOpen('editCreateLoad');
    }

    const handleDeliveryLogOpen = (load: Load) => {
        setSingleSelectedLoad(load);
        setDialogOpen('deliveryLog');
    }

    const handleUnmergeOpen = (load: Load) => {
        setSingleSelectedLoad(load);
        setDialogOpen('unmergeLoad');
    }

    const { columns, isLoadingColumns } = useDraftLoadColumns(isDrafts, handleEditableLoadOpen, handleDeliveryLogOpen, handleUnmergeOpen, applyDefaultPreset, setApplyDefaultPreset);
    const { table, rowSelectionOrder } = useDraftLoadTable(isDrafts, columns, draftsLoadsQuery.data || []);

    const { user } = useUser();

    // resetColumnFilters is special
    // rule is it only gets reset when switching pages, but not when using date filters.
    // Once reset in useEffect on isDrafts change. The applyDefaultPrest logic will reload
    // the proper default presets (if any)
    const resetTableState = () => {
        table.setPagination({
            pageIndex: 0,
            pageSize: getPageSize(isDrafts, user),
        });
        table.resetRowSelection(true);
        globalFilterRemover(table, 'selectedFilter');
        table.resetExpanded(true);
    }

    useEffect(() => {
        resetTableState();
        table.resetColumnFilters(true);
        setApplyDefaultPreset(true);
    }, [isDrafts]);

    const isLoading = draftsLoadsQuery.isLoading || isLoadingColumns;
    const isFetching = draftsLoadsQuery.isFetching;

    return (
        <DraftsLoadsProvider isDrafts={isDrafts}>
            <ContentHeader>
                <div className='lt-header-container'>
                    <Typography variant='h5'>{isDrafts ? L.drafts() : L.loads()} - {user.organization.name}</Typography>
                    {!isDrafts && canCreateOrder && !isCarrier &&
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => setDialogOpen('editCreateLoad')}
                            startIcon={<AddIcon />}
                        >
                            {L.createLoad()}
                        </Button>
                    }
                    {isDrafts && canCreateOrder && !isCarrier &&
                        <MenuButton
                            label={L.createDraftParenthetical()}
                            ButtonProps={{
                                variant: 'contained',
                                color: 'primary',
                                startIcon: <AddIcon />,
                            }}
                        >
                            <MenuItem onClick={() => setDialogOpen('createDraft')}>{L.singleDraft()}</MenuItem>
                            <MenuItem onClick={() => setDialogOpen('createDrafts')}>{L.multipleDrafts()}</MenuItem>
                        </MenuButton>
                    }
                    <SelectionButtons
                        isLoading={isLoading}
                        table={table}
                        setDialogOpen={setDialogOpen}
                        rowSelectionOrder={rowSelectionOrder}
                    />
                </div>
            </ContentHeader>
            <DraftLoadModals
                table={table}
                isLoading={isLoading}
                rowSelectionOrder={rowSelectionOrder}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                singleSelectedLoad={singleSelectedLoad}
                setSingleSelectedLoad={setSingleSelectedLoad}
            />
            <Content style={{ height: '100%', overflowY: 'hidden' }}>
                <Paper
                    className='lt-mobile'
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                    <SocketListener />
                    <LoadTableToolbar
                        draftsLoadsQueryRefetch={draftsLoadsQuery.refetch}
                        table={table}
                        resetTableState={resetTableState}
                    />
                    <DraftLoadTable
                        isLoading={isLoading}
                        isFetching={isFetching}
                        table={table}
                    />
                </Paper>
            </Content>
        </DraftsLoadsProvider>
    );
};
